import React, { useEffect, useMemo, useState } from 'react';
import './SupplierMenu.scss';
import '../../resources/main.scss';

const normalizeCategoryPath = (value) => String(value || '')
  .trim()
  .replace(/\\/g, '/')
  .replace(/\s*\/\s*/g, '/')
  .replace(/^\/+|\/+$/g, '')
  .replace(/\/{2,}/g, '/');

const SELF_SELECTION_PREFIX = '__self__:';

const normalizeSelectableValue = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '';
  if (rawValue === 'all') return 'all';

  if (rawValue.startsWith(SELF_SELECTION_PREFIX)) {
    const ownPath = normalizeCategoryPath(rawValue.slice(SELF_SELECTION_PREFIX.length));
    return ownPath ? `${SELF_SELECTION_PREFIX}${ownPath}` : '';
  }

  return normalizeCategoryPath(rawValue);
};

const formatCategoryLabel = (value, shouldUppercase = false) => {
  const normalizedLabel = String(value || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (!normalizedLabel) {
    return '';
  }

  if (!shouldUppercase) {
    return normalizedLabel;
  }

  return normalizedLabel
    .toLowerCase()
    .replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
};

const sortNodesAlphabetically = (nodes) => [...nodes].sort((firstNode, secondNode) =>
  firstNode.displayLabel.localeCompare(secondNode.displayLabel, 'ro', { sensitivity: 'base' })
);

const SupplierMenu = ({ categoryOptions, selectedCategories, onCategoriesChange }) => {
  const [internalSelectedCategories, setInternalSelectedCategories] = useState(['all']);
  const [expandedNodePaths, setExpandedNodePaths] = useState([]);
  const isControlled = Array.isArray(selectedCategories) && typeof onCategoriesChange === 'function';

  const effectiveSelectedCategories = isControlled ? selectedCategories : internalSelectedCategories;
  const setEffectiveSelectedCategories = isControlled ? onCategoriesChange : setInternalSelectedCategories;

  const treeData = useMemo(() => {
    const normalizedCategories = Array.isArray(categoryOptions)
      ? categoryOptions
        .map((option) => {
          const fullPath = normalizeCategoryPath(option?.fullPath || option?.value || option?.label);
          if (!fullPath) return null;
          return {
            fullPath,
            label: String(option?.label || fullPath).trim(),
            count: Number.isFinite(Number(option?.count)) ? Number(option.count) : undefined
          };
        })
        .filter(Boolean)
      : [];

    const nodeMap = new Map();
    const rootNodePaths = [];

    normalizedCategories.forEach((categoryEntry) => {
      const pathParts = categoryEntry.fullPath.split('/').filter(Boolean);
      pathParts.forEach((pathPart, index) => {
        const nodePath = pathParts.slice(0, index + 1).join('/');
        const parentPath = index > 0 ? pathParts.slice(0, index).join('/') : null;
        if (!nodeMap.has(nodePath)) {
          nodeMap.set(nodePath, {
            path: nodePath,
            label: pathPart,
            parentPath,
            depth: index,
            isLeaf: false,
            count: undefined,
            children: []
          });
        }

        if (!parentPath && !rootNodePaths.includes(nodePath)) {
          rootNodePaths.push(nodePath);
        }

        if (parentPath) {
          const parentNode = nodeMap.get(parentPath);
          if (parentNode && !parentNode.children.includes(nodePath)) {
            parentNode.children.push(nodePath);
          }
        }
      });

      const leafPath = pathParts.join('/');
      if (!leafPath) return;
      const leafNode = nodeMap.get(leafPath);
      if (leafNode) {
        leafNode.isLeaf = true;
        leafNode.label = categoryEntry.label || leafNode.label;
        if (Number.isFinite(categoryEntry.count)) {
          leafNode.count = (leafNode.count || 0) + categoryEntry.count;
        }
      }
    });

    const buildNode = (nodePath) => {
      const node = nodeMap.get(nodePath);
      if (!node) return null;
      const hasChildren = node.children.length > 0;
      const ownCount = Number.isFinite(node.count) ? node.count : 0;
      const builtChildren = sortNodesAlphabetically(node.children.map(buildNode).filter(Boolean));
      const shouldAppendOtherNode = hasChildren && ownCount > 0;

      if (shouldAppendOtherNode) {
        builtChildren.push({
          path: `${node.path}/__other__`,
          label: 'Altele',
          displayLabel: 'Altele',
          parentPath: node.path,
          depth: node.depth + 1,
          isLeaf: true,
          isSyntheticOther: true,
          sourcePath: node.path,
          count: ownCount,
          children: []
        });
      }

      return {
        ...node,
        displayLabel: formatCategoryLabel(node.label, hasChildren),
        children: builtChildren
      };
    };

    const rootNodes = sortNodesAlphabetically(rootNodePaths.map(buildNode).filter(Boolean));
    const selectableValuesByNode = new Map();
    const allSelectableValues = [];
    const aggregatedCountByNode = new Map();

    const collectSelectableValues = (node) => {
      if (!node) return [];
      if (!node.children.length) {
        const selectableValue = node.isSyntheticOther
          ? `${SELF_SELECTION_PREFIX}${node.sourcePath}`
          : node.path;
        selectableValuesByNode.set(node.path, [selectableValue]);
        allSelectableValues.push(selectableValue);
        aggregatedCountByNode.set(node.path, Number.isFinite(node.count) ? node.count : 0);
        return [selectableValue];
      }

      const descendantSelectableValues = node.children.flatMap((childNode) => collectSelectableValues(childNode));
      selectableValuesByNode.set(node.path, descendantSelectableValues);
      const descendantCount = node.children.reduce(
        (totalCount, childNode) => totalCount + (aggregatedCountByNode.get(childNode.path) || 0),
        0
      );
      aggregatedCountByNode.set(node.path, descendantCount);
      return descendantSelectableValues;
    };

    rootNodes.forEach((rootNode) => collectSelectableValues(rootNode));

    return {
      rootNodes,
      selectableValuesByNode,
      aggregatedCountByNode,
      selectableValueSet: new Set(allSelectableValues)
    };
  }, [categoryOptions]);

  useEffect(() => {
    const sanitizedSelection = effectiveSelectedCategories.filter((value) =>
      value === 'all' || treeData.selectableValueSet.has(normalizeSelectableValue(value))
    ).map((value) => normalizeSelectableValue(value));
    const nextSelection = sanitizedSelection.length ? sanitizedSelection : ['all'];
    const shouldUpdateSelection =
      nextSelection.length !== effectiveSelectedCategories.length ||
      nextSelection.some((value, index) => value !== effectiveSelectedCategories[index]);

    if (shouldUpdateSelection) {
      setEffectiveSelectedCategories(nextSelection);
    }
  }, [treeData.selectableValueSet, effectiveSelectedCategories, setEffectiveSelectedCategories]);

  const toggleNodeSelection = (nodePath) => {
    if (nodePath === 'all') {
      setEffectiveSelectedCategories(['all']);
      return;
    }

    const descendantSelectableValues = treeData.selectableValuesByNode.get(nodePath) || [nodePath];
    const currentSelection = new Set(
      effectiveSelectedCategories
        .filter((value) => value !== 'all')
        .map((value) => normalizeSelectableValue(value))
    );
    const areAllDescendantsSelected = descendantSelectableValues.every((selectableValue) =>
      currentSelection.has(selectableValue)
    );

    if (areAllDescendantsSelected) {
      descendantSelectableValues.forEach((selectableValue) => currentSelection.delete(selectableValue));
    } else {
      descendantSelectableValues.forEach((selectableValue) => currentSelection.add(selectableValue));
    }

    const nextSelection = Array.from(currentSelection);
    setEffectiveSelectedCategories(nextSelection.length ? nextSelection : ['all']);
  };

  const normalizedSelection = useMemo(
    () => new Set(
      effectiveSelectedCategories
        .filter((value) => value !== 'all')
        .map((value) => normalizeSelectableValue(value))
    ),
    [effectiveSelectedCategories]
  );

  const isNodeChecked = (nodePath) => {
    const descendantSelectableValues = treeData.selectableValuesByNode.get(nodePath) || [];
    return descendantSelectableValues.length > 0 &&
      descendantSelectableValues.every((selectableValue) => normalizedSelection.has(selectableValue));
  };

  const toggleNodeExpanded = (nodePath) => {
    setExpandedNodePaths((previousExpandedPaths) =>
      previousExpandedPaths.includes(nodePath)
        ? previousExpandedPaths.filter((existingPath) => existingPath !== nodePath)
        : [...previousExpandedPaths, nodePath]
    );
  };

  const isNodeExpanded = (nodePath) => expandedNodePaths.includes(nodePath);

  const renderNode = (node) => {
    const descendantSelectableValues = treeData.selectableValuesByNode.get(node.path) || [];
    const hasPartialSelection =
      !isNodeChecked(node.path) &&
      descendantSelectableValues.some((selectableValue) => normalizedSelection.has(selectableValue));

    return (
      <div key={node.path} className="supplier-menu__tree-node-wrapper">
        <div className="supplier-menu__tree-row" style={{ paddingLeft: `${node.depth * 14}px` }}>
          <button
            type="button"
            className={`supplier-menu__expand-toggle ${node.children.length === 0 ? 'supplier-menu__expand-toggle--hidden' : ''}`}
            onClick={() => toggleNodeExpanded(node.path)}
            disabled={node.children.length === 0}
            aria-label={isNodeExpanded(node.path) ? `Restrange ${node.displayLabel}` : `Extinde ${node.displayLabel}`}
          >
            {isNodeExpanded(node.path) ? '▾' : '▸'}
          </button>
          <label className="supplier-menu__checkbox supplier-menu__tree-node">
            <input
              type="checkbox"
              value={node.path}
              checked={isNodeChecked(node.path)}
              onChange={() => toggleNodeSelection(node.path)}
              ref={(inputElement) => {
                if (inputElement) {
                  inputElement.indeterminate = hasPartialSelection;
                }
              }}
            />
            <span className="supplier-menu__checkbox-label" title={node.displayLabel}>
              {node.displayLabel}
              {Number.isFinite(treeData.aggregatedCountByNode.get(node.path))
                ? ` (${treeData.aggregatedCountByNode.get(node.path)})`
                : ''}
            </span>
          </label>
        </div>
        {node.children.length > 0 && isNodeExpanded(node.path) && (
          <div className="supplier-menu__tree-children">
            {node.children.map((childNode) => renderNode(childNode))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='supplier-menu__wrapper standard hp'>
      <div className="supplier-menu__mobile-header"></div>
      <div className='supplier-menu'>
        <h2 className="supplier-menu__title">Filtre</h2>
        <h3 className="supplier-menu__subtitle">Categorie</h3>
        <div className='supplier-menu__links-wrapper'>
          <label className="supplier-menu__checkbox supplier-menu__tree-node">
            <input
              type="checkbox"
              value="all"
              checked={effectiveSelectedCategories.includes('all')}
              onChange={() => toggleNodeSelection('all')}
            />
            <span className="supplier-menu__checkbox-label" title="Toate">Toate</span>
          </label>
          {treeData.rootNodes.map((rootNode) => renderNode(rootNode))}
        </div>
      </div>
    </div>
  );
};

export default SupplierMenu;